import React, { useEffect, useState } from "react";
import { IoChevronBackOutline } from "react-icons/io5";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FormControlLabel, Switch } from "@mui/material";
import { Button } from "@material-tailwind/react";
import toast from "react-hot-toast";
import { postServices, putServices } from "../../apiServices/apiServices";

function ConfigureGLD() {
  const [loading, setLoading] = useState({ btn: false });
  const [isToggleValue, setIsToggleValue] = useState(false);
  const [fieldData, setFieldData] = useState({
    threshold_lw: "",
    threshold_hi: "",
    zone_count: "",
    zones: [],
    sensor_count: [],
    sensors: [],
  });
  const location = useLocation().state;
  const navigate = useNavigate();
  const access_token = localStorage.getItem("gassol-admin-token");

  useEffect(() => {
    const zonesData =
      location?.controllerDetails?.sensorInfo?.[0]?.zones?.map(
        (zone) => zone.zone_name,
      ) || [];
    const sensorCount =
      location?.controllerDetails?.sensorInfo?.[0]?.zones?.map(
        (zone) => zone.sensors?.length,
      ) || [];
    const sensorData =
      location?.controllerDetails?.sensorInfo?.[0]?.zones?.map(
        (zone) => zone.sensors,
      ) || [];

    setIsToggleValue(
      location?.controllerDetails?.reportInfo?.[0]?.gld_report === 1
        ? true
        : false,
    );
    setFieldData({
      ...fieldData,
      threshold_lw:
        location?.controllerDetails?.sensorInfo?.[0]?.threshold_lw ?? "",
      threshold_hi:
        location?.controllerDetails?.sensorInfo?.[0]?.threshold_hi ?? "",
      zone_count:
        location?.controllerDetails?.sensorInfo?.[0]?.zone_count ?? "",
      zones: zonesData,
      sensor_count: sensorCount,
      sensors: sensorData,
    });
  }, []);

  const handleSensorCountChange = (e, zoneIndex) => {
    const value = parseInt(e.target.value, 10) || 0; // Parse the value to integer, default to 0 if invalid input

    // Clone the sensor_count array
    const updatedSensorCount = [...fieldData.sensor_count];
    updatedSensorCount[zoneIndex] = value;

    // Clone and update the sensors array
    const updatedSensors = [...fieldData.sensors];

    // Ensure the zone array exists
    if (!updatedSensors[zoneIndex]) updatedSensors[zoneIndex] = [];

    // Handle reduction in sensor count (trim the sensors array)
    if (value === 0) {
      updatedSensors[zoneIndex] = []; // Clear the sensors if sensor count is 0
    } else {
      // If sensor count is increased, we append empty sensors to match the count
      updatedSensors[zoneIndex] = updatedSensors[zoneIndex].slice(0, value); // Trim excess sensors
      while (updatedSensors[zoneIndex].length < value) {
        updatedSensors[zoneIndex].push(""); // Add empty sensors for the missing count
      }
    }

    setFieldData({
      ...fieldData,
      sensor_count: updatedSensorCount,
      sensors: updatedSensors,
    });
  };

  const handleZoneCountChange = (e) => {
    const inputValue = Number(e.target.value);

    if (inputValue >= 1 && inputValue <= 32) {
      // Create new arrays for zones and sensor counts based on the new zone count
      const newZones = Array.from(
        { length: inputValue },
        (_, i) => fieldData.zones[i] || "",
      );
      const newSensorCounts = Array.from(
        { length: inputValue },
        (_, i) => fieldData.sensor_count[i] || null,
      );
      const newSensors = Array.from(
        { length: inputValue },
        (_, i) => fieldData.sensors[i] || [],
      ); // Initialize the sensors array for each zone

      setFieldData({
        ...fieldData,
        zone_count: inputValue,
        zones: newZones,
        sensor_count: newSensorCounts,
        sensors: newSensors, // Ensure sensors array is updated
      });
    } else if (e.target.value === "") {
      setFieldData({
        ...fieldData,
        zone_count: "",
        zones: [],
        sensor_count: [],
        sensors: [],
      });
    }
  };

  const handleZonesChange = (e, i) => {
    const value = e.target.value;

    const updatedZones = [...fieldData.zones];
    updatedZones[i] = value || "";

    setFieldData({
      ...fieldData,
      zones: updatedZones,
    });
  };

  const handleSensorValueChange = (e, zoneIndex, sensorIndex) => {
    const value = parseInt(e.target.value, 10) || ""; // Convert to int, or default to an empty value
    const updatedSensors = [...fieldData.sensors];

    // Ensure the zone array exists
    if (!updatedSensors[zoneIndex]) updatedSensors[zoneIndex] = [];
    updatedSensors[zoneIndex][sensorIndex] = value;

    setFieldData({
      ...fieldData,
      sensors: updatedSensors,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    try {
      const res = await putServices(
        `/api/hardware/activate/update?hardware_id=${location?.controllerDetails?.hardware_id}`,
        { gld_report: 1 },
        access_token,
      );
      if (res.responseCode === 200) {
        handleSetSensorInfo();
      } else {
        toast.error(res.responseMessage);
      }
    } catch (error) {
      setLoading({ ...loading, btn: false });
      toast.error(error);
    }
  };

  const handleSetSensorInfo = async () => {
    setLoading({ ...loading, btn: true });
    try {
      const res = await postServices(
        `/api/hardware/setsensorinfo`,
        {
          hardware_id: location?.controllerDetails?.hardware_id,
          threshold_lw: fieldData.threshold_lw,
          threshold_hi: fieldData.threshold_hi,
          zone_count: fieldData.zone_count,
          zones: fieldData.zones,
          sensors: fieldData.sensors,
        },
        access_token,
      );
      setLoading({ ...loading, btn: false });
      if (res.responseCode === 200) {
        navigate(-1);
      } else {
        toast.error(res.responseMessage);
      }
    } catch (error) {
      setLoading({ ...loading, btn: false });
      toast.error(error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      const res = await putServices(
        `/api/hardware/activate/update?hardware_id=${location?.controllerDetails?.hardware_id}`,
        {
          zone_count: fieldData.zone_count,
          zones: fieldData.zones,
          sensors: fieldData.sensors,
          threshold_lw: fieldData.threshold_lw,
          threshold_hi: fieldData.threshold_hi,
        },
        access_token,
      );
      if (res.responseCode === 200) {
        navigate(-1);
      } else {
        toast.error(res.responseMessage);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const handleToggleOff = async () => {
    try {
      const res = await putServices(
        `/api/hardware/activate/update?hardware_id=${location?.controllerDetails?.hardware_id}`,
        { gld_report: 0 },
        access_token,
      );
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <>
      <div className="py-4 md:py-6 max-w-[1400px] mx-auto px-3">
        <>
          <div className="flex gap-2 justify-between items-center flex-row">
            <div className="flex justify-between gap-3 sm:gap-6">
              <Link
                to={-1}
                className="flex gap-2 items-center font-semibold text-xl text-[#BDBDBD]"
              >
                <IoChevronBackOutline />
                <p>GLD Configuration</p>
              </Link>
            </div>
            <FormControlLabel
              control={<Switch sx={{ m: 1 }} />}
              checked={isToggleValue}
              onChange={(e) => {
                if (e.target.checked) {
                  setIsToggleValue(e.target.checked);
                } else {
                  setIsToggleValue(e.target.checked);
                  handleToggleOff();
                }
              }}
            />
          </div>

          {isToggleValue && (
            <form
              className="mt-5"
              onSubmit={
                location?.controllerDetails?.reportInfo?.[0]?.gld_report === 1
                  ? handleUpdate
                  : handleSubmit
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-3 items-center justify-between gap-4">
                <div>
                  <label className="text-black font-semibold">
                    High Threshold *
                  </label>
                  <input
                    className="w-full bg-[#F5F7F9] px-7 py-6 outline-none border-none rounded-2xl text-[#8f8f8f] custom-number-input"
                    type="number"
                    placeholder="High Threshold *"
                    value={fieldData.threshold_hi}
                    onChange={(e) =>
                      setFieldData({
                        ...fieldData,
                        threshold_hi: Number(e.target.value) || "",
                      })
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "-" ||
                        e.key === "+"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="text-black font-semibold">
                    Low Threshold *
                  </label>
                  <input
                    className="w-full bg-[#F5F7F9] px-7 py-6 outline-none border-none rounded-2xl text-[#8f8f8f] custom-number-input"
                    type="number"
                    placeholder="Low Threshold *"
                    value={fieldData.threshold_lw}
                    onChange={(e) =>
                      setFieldData({
                        ...fieldData,
                        threshold_lw: Number(e.target.value) || "",
                      })
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "-" ||
                        e.key === "+"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="text-black font-semibold">
                    Total Zones *
                  </label>
                  <input
                    className="w-full bg-[#F5F7F9] px-7 py-6 outline-none border-none rounded-2xl text-[#8f8f8f] custom-number-input"
                    type="number"
                    placeholder="Total Zones *"
                    value={fieldData.zone_count}
                    onChange={handleZoneCountChange}
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "-" ||
                        e.key === "+"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    min={1}
                    max={32}
                    maxLength={2}
                    required
                  />
                </div>
              </div>

              {Array.from({ length: fieldData.zone_count }, (_, zoneIndex) => (
                <div
                  className="grid grid-cols-1 md:grid-cols-3 items-start justify-between gap-4 mt-3"
                  key={zoneIndex}
                >
                  <div>
                    <label className="text-black font-semibold">
                      Enter the Zone name *
                    </label>
                    <input
                      className="w-full bg-[#F5F7F9] px-7 py-6 outline-none border-none rounded-2xl text-[#8f8f8f] custom-number-input"
                      type="text"
                      placeholder="Enter the Zone name *"
                      value={fieldData.zones[zoneIndex] || ""}
                      onChange={(e) => handleZonesChange(e, zoneIndex)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-black font-semibold">
                      Total Sensors *
                    </label>
                    <input
                      className="w-full bg-[#F5F7F9] px-7 py-6 outline-none border-none rounded-2xl text-[#8f8f8f] custom-number-input"
                      type="number"
                      placeholder="Total Sensors *"
                      value={fieldData.sensor_count[zoneIndex] ?? ""}
                      onChange={(e) => handleSensorCountChange(e, zoneIndex)}
                      onKeyDown={(e) => {
                        if (
                          e.key === "e" ||
                          e.key === "E" ||
                          e.key === "-" ||
                          e.key === "+"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      min={1}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {fieldData.sensor_count &&
                      Array.from(
                        { length: Number(fieldData.sensor_count[zoneIndex]) },
                        (_, index) => index + 1,
                      ).map((item, sensorIndex) => (
                        <div
                          key={sensorIndex}
                          className="min-w-[auto] gld-select-sensors-dropdown"
                        >
                          <div>
                            <label className="text-black font-semibold">
                              Sens {sensorIndex + 1} *
                            </label>
                            <input
                              className="w-full bg-[#F5F7F9] px-4 py-3 outline-none border-none rounded-lg text-[#8f8f8f] custom-number-input"
                              type="number"
                              placeholder={`Sens ${sensorIndex + 1} *`}
                              value={
                                fieldData.sensors?.[zoneIndex]?.[sensorIndex] ??
                                ""
                              }
                              onChange={(e) =>
                                handleSensorValueChange(
                                  e,
                                  zoneIndex,
                                  sensorIndex,
                                )
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "e" ||
                                  e.key === "E" ||
                                  e.key === "-" ||
                                  e.key === "+"
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              onWheel={(e) => e.target.blur()}
                              min={1}
                              required
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              <div className="my-3 flex justify-end items-center">
                <Button
                  type="submit"
                  className="font-poppins font-medium tracking-wider bg-[#03438D] disabled:cursor-not-allowed disabled:bg-slate-500"
                  disabled={loading.btn}
                >
                  {!loading.btn
                    ? location?.controllerDetails?.reportInfo?.[0]
                        ?.gld_report === 1
                      ? "Update"
                      : "Save"
                    : "Loading..."}
                  {/* {!loading.btn
                  ? location.isUpdate
                    ? "Update"
                    : "Save"
                  : "Loading..."} */}
                </Button>
              </div>
            </form>
          )}
        </>
      </div>
    </>
  );
}

export default ConfigureGLD;
